/* eslint-disable import/no-internal-modules -- reuses the events table's model and cells */
import { Loading } from '@livecontrol/core-ui';
import { DateTime } from 'luxon';
import type { ReactElement } from 'react';
import { useCallback, useEffect, useState } from 'react';
import useInfiniteScroll from 'react-infinite-scroll-hook';
import { useHistory } from 'react-router-dom';
import styled from 'styled-components';
import type { SelectOption } from '../../components';
import { HighlightText, Panel, Select, SmartSearch, Tag } from '../../components';
import { Destinations } from '../events/destinations';
import { eventTypeInfo, formatTime, STATUS } from '../events/event-model';
import { EventType } from '../events/graphql';
import type { LiveLiteEvent } from './use-live-lite-events';
import { useLiveLiteEvents } from './use-live-lite-events';

const SEARCH_DEBOUNCE_MS = 300;

const EVENT_TYPE_OPTIONS: SelectOption<EventType | undefined>[] = [
  { value: undefined, label: 'All Events' },
  { value: EventType.Produced, label: 'Produced' },
  { value: EventType.Static, label: 'Static' },
  { value: EventType.Mobile, label: 'Mobile' },
  { value: EventType.MobileKit, label: 'Mobile Kit' },
  { value: EventType.SimLive, label: 'Simulated Live' },
  { value: EventType.Test, label: 'Test' }
];

const TopBar = styled.div`
  display: flex;
  justify-content: flex-end;
  padding: 12px 2px;

  .tz {
    color: var(--white40);
    font-size: 13px;
    white-space: nowrap;

    b {
      font-weight: 700;
    }
  }
`;

const Toolbar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
  margin-bottom: 16px;

  h1 {
    margin: 0;
    font-size: 20px;
    font-weight: 700;
    color: var(--white);
  }

  .filters {
    display: flex;
    align-items: center;
    gap: 12px;
    flex-wrap: wrap;
  }

  .search {
    width: 250px;
  }
`;

// Same look as the events table view (routes/events/table.tsx), minus favorites, alerts and navigation.
const Table = styled.table`
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 13px;
  color: var(--white);

  thead th {
    padding: 12px 7px;
    text-align: left;
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: var(--white35);
    white-space: nowrap;
  }

  tbody td {
    padding: 13px 7px;
    border-top: 1px solid rgba(255, 255, 255, 0.06);
    vertical-align: middle;
    font-size: 13px;
  }

  th:first-child,
  td:first-child {
    padding-left: 18px;
  }

  th:last-child,
  td:last-child {
    padding-right: 18px;
    text-align: right;
  }

  .flags {
    display: flex;
    align-items: center;
    gap: 6px;
    flex-wrap: wrap;
  }

  .status {
    text-transform: uppercase;
  }

  .title {
    font-weight: 700;
  }

  .ids {
    display: flex;
    gap: 10px;
    margin-top: 4px;
    font-family: 'JetBrains Mono', monospace;
    font-size: 11px;
    color: var(--white60);
  }

  .time {
    color: var(--white60);
    white-space: nowrap;
  }

  /* Client on top, its location on the second line — same stacked shape as the event cell. */
  .client .name {
    font-weight: 500;
  }

  .client .location {
    margin-top: 4px;
    font-size: 11px;
    color: var(--white60);
  }
`;

const Row = styled.tr<{ $highPriority: boolean }>`
  cursor: pointer;
  transition: background var(--transition);

  &:hover {
    background: ${({ $highPriority }): string =>
      $highPriority ? 'rgba(255, 80, 95, 0.1)' : 'rgba(255, 255, 255, 0.03)'};
  }

  ${({ $highPriority }): string =>
    $highPriority
      ? 'box-shadow: rgb(255, 80, 95) 3px 0px 0px 0px inset; background: rgba(255, 80, 95, 0.06);'
      : ''}
`;

const Footer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 16px;
  color: var(--white40);
  font-size: 12px;
`;

const EventRow = ({
  event,
  search,
  onOpen
}: {
  event: LiveLiteEvent;
  search?: string;
  onOpen: (eid: string) => void;
}): ReactElement => {
  const start = DateTime.fromISO(event.startTime ?? '');
  const end = DateTime.fromISO(event.endTime ?? '');
  const cid = event.client?.cid ? `${event.client.cid}.${event.location_secondary_id ?? ''}` : '';
  // The client has its own column now, so an unnamed event no longer falls back to it.
  const title = event.name ?? 'Untitled';

  return (
    <Row
      $highPriority={!!event.high_priority}
      onClick={(): void => {
        onOpen(event.eid);
      }}
    >
      <td className='status'>
        <Tag
          size='small'
          className='cc-green'
          style={{ border: 'none' }}
          icon='fas fa-circle cc-blink-slow'
          iconSize={7}
        >
          {STATUS.live.label}
        </Tag>
      </td>

      <td>
        <div className='title'>
          <HighlightText highlight={search}>{title}</HighlightText>
        </div>
        <div className='ids'>
          {cid && (
            <span>
              CID: <HighlightText highlight={search}>{cid}</HighlightText>
            </span>
          )}
          <span>
            EID: <HighlightText highlight={search}>{event.eid}</HighlightText>
          </span>
        </div>
      </td>

      <td className='client'>
        <div className='name'>
          <HighlightText highlight={search}>{event.client?.organizationName ?? ''}</HighlightText>
        </div>
        {!!event.location_name && <div className='location'>{event.location_name}</div>}
      </td>

      <td className='time'>{formatTime(start, end)}</td>

      <td>
        <Tag size='small'>{eventTypeInfo(event.eventType ?? undefined).label}</Tag>
      </td>

      <td>
        <div className='flags'>
          {event.receive_iso_recordings && (
            <Tag size='small' className='cc-neutral'>
              ISO
            </Tag>
          )}
          {!!event.audio_cue_sheet_url && (
            <Tag size='small' className='cc-neutral'>
              Audio Mix
            </Tag>
          )}
          {!!event.operator_name && (
            <Tag size='small' className='cc-neutral'>
              DVG
            </Tag>
          )}
        </div>
      </td>

      <td>
        <Destinations {...event} />
      </td>
    </Row>
  );
};

/*
 * Live events only, without the sidebar, loading a page at a time as you scroll — the same way the full
 * events list does. Reached by typing the URL; nothing in the rest of the Command Center links here.
 * Filters are local so they do not touch the events page's persisted filters.
 */
export const LiveLite = (): ReactElement => {
  const history = useHistory();
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState<string>();
  const [eventType, setEventType] = useState<EventType>();

  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(searchInput.trim() || undefined);
    }, SEARCH_DEBOUNCE_MS);

    return (): void => {
      clearTimeout(timer);
    };
  }, [searchInput]);

  const { events, error, refetch, hasMore, loaded, loading, total, loadMore } = useLiveLiteEvents({
    search,
    eventType
  });

  const [sentryRef] = useInfiniteScroll({
    loading,
    hasNextPage: hasMore,
    onLoadMore: loadMore,
    rootMargin: '0px 0px 400px 0px'
  });

  /* Stays inside the standalone pages: the full event page is the one at /event/:eid. */
  const openEvent = useCallback(
    (eid: string): void => {
      history.push(`/lite/${eid}`);
    },
    [history]
  );

  let content: ReactElement;

  if (error && !loaded) {
    content = (
      <Panel className='text-center p-16px text-danger'>
        Error trying to load events.{' '}
        <button
          type='button'
          className='x-pseudo text-white text-decoration-underline'
          onClick={(): void => {
            void refetch();
          }}
        >
          Click Here to retry
        </button>
      </Panel>
    );
  } else if (!loaded) {
    content = <Loading isOldVersion />;
  } else if (events.length === 0) {
    content = (
      <Panel className='text-center p-16px text-white'>
        {search || eventType ? 'No live events match your filters.' : 'No live events right now.'}
      </Panel>
    );
  } else {
    content = (
      <Table>
        <thead>
          <tr>
            <th>Status</th>
            <th>Event</th>
            <th>Client</th>
            <th>Time</th>
            <th>Setup</th>
            <th>Flags</th>
            <th>Destinations</th>
          </tr>
        </thead>
        <tbody>
          {events.map((event) => (
            <EventRow key={event.eid} event={event} search={search} onOpen={openEvent} />
          ))}
        </tbody>
      </Table>
    );
  }

  return (
    <div>
      <TopBar>
        <div className='tz'>
          <b>Time Zone:</b> {DateTime.local().toFormat('z')}
        </div>
      </TopBar>

      <Toolbar>
        <h1>Live Events{total === undefined ? '' : ` (${total})`}</h1>

        <div className='filters'>
          <div className='search'>
            <SmartSearch search={searchInput} onSearch={setSearchInput} />
          </div>
          <Select options={EVENT_TYPE_OPTIONS} value={eventType} onChange={setEventType} />
        </div>
      </Toolbar>

      {content}

      {/*
        * The sentry loads the next page when it scrolls into view, as on the full events list.
        */}
      {loaded && hasMore && (
        <div ref={sentryRef} className='mt-12px'>
          <Loading isOldVersion />
        </div>
      )}

      {loaded && !hasMore && events.length > 0 && (
        <Footer>
          {events.length} live event{events.length === 1 ? '' : 's'}
        </Footer>
      )}
    </div>
  );
};
